import { useEffect, useId, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { SelectionDrawerProps } from './BottomSheetSelectDrawer';

/** Keep the header usable before the optional selection dialog is downloaded. */
export default function BottomSheetSelect<T extends string>({
  label,
  description,
  value,
  options,
  onSelect,
  disabled,
  triggerContent,
  triggerClassName,
  triggerAriaLabel,
  prepare,
}: {
  label: string;
  description: string;
  value: T;
  options: readonly { value: T; label: string; disabled?: boolean }[];
  onSelect: (value: T) => void;
  disabled?: boolean;
  triggerContent?: ReactNode;
  triggerClassName?: string;
  triggerAriaLabel?: string;
  prepare?: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const trigger = useRef<HTMLButtonElement>(null);
  const request = useRef({ revision: 0 });
  const dialogId = useId();
  const [Renderer, setRenderer] = useState<ComponentType<
    SelectionDrawerProps<T>
  > | null>(null);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'failed'>('idle');

  useEffect(() => {
    const lifetime = request.current;
    const discard = () => {
      request.current.revision++;
      setOpen(false);
      setPhase('idle');
    };
    const hidden = () => {
      if (document.visibilityState === 'hidden') discard();
    };
    window.addEventListener('pagehide', discard);
    document.addEventListener('visibilitychange', hidden);
    return () => {
      lifetime.revision++;
      window.removeEventListener('pagehide', discard);
      document.removeEventListener('visibilitychange', hidden);
    };
  }, []);

  useEffect(() => {
    if (phase === 'idle') return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      request.current.revision++;
      setPhase('idle');
      trigger.current?.focus({ preventScroll: true });
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [phase]);

  const dismiss = () => {
    request.current.revision++;
    setPhase('idle');
    trigger.current?.focus({ preventScroll: true });
  };
  const show = async () => {
    if (disabled) return;
    if (phase === 'loading') {
      dismiss();
      return;
    }
    if (Renderer) {
      setOpen(true);
      return;
    }
    const originalRequest = ++request.current.revision;
    setPhase('loading');
    try {
      const [module] = await Promise.all([
        import('./BottomSheetSelectDrawer'),
        prepare?.(),
      ]);
      if (request.current.revision !== originalRequest) return;
      setRenderer(() => module.default);
      setPhase('idle');
      setOpen(true);
    } catch {
      if (request.current.revision !== originalRequest) return;
      setPhase('failed');
    }
  };

  return (
    <>
      <Button
        ref={trigger}
        type="button"
        variant="outline"
        disabled={disabled}
        aria-label={triggerAriaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        aria-busy={phase === 'loading' || undefined}
        className={cn(
          'max-w-full whitespace-normal aria-busy:bg-gp-pink-100',
          triggerClassName,
        )}
        onClick={() => void show()}
      >
        {triggerContent ??
          `${label}: ${options.find((option) => option.value === value)?.label ?? ''}`}
      </Button>
      {phase === 'loading' && (
        <span role="status" className="sr-only">
          {t('status.loading')}
        </span>
      )}
      {phase === 'failed' && (
        <div className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-sm rounded-card border border-gp-pink-100 bg-gp-cream p-4 text-body text-gp-ink-900 shadow-glow">
          <p role="alert">{t('status.error')}</p>
          <div className="mt-3 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={dismiss}>
              {t('header.close')}
            </Button>
            <Button type="button" onClick={() => window.location.reload()}>
              {t('entry.retry')}
            </Button>
          </div>
        </div>
      )}
      {Renderer && (
        <Renderer
          label={label}
          description={description}
          value={value}
          options={options}
          onSelect={onSelect}
          open={open}
          onOpenChange={setOpen}
          trigger={trigger}
          dialogId={dialogId}
        />
      )}
    </>
  );
}
